import { Button, Snackbar } from '@nrx/ui';
import { useCallback, useEffect, useState } from 'react';

import { useFileUpload } from '../hooks/use-file-upload.js';
import type { FxFile, FxFilesClient, Segment } from '../lib/client.js';

import { FileTable } from './FileTable.js';
import { UploadDropzone } from './UploadDropzone.js';

export interface FileExchangeViewProps {
  readonly client: FxFilesClient;
  readonly openUrl?: (url: string) => void;
  /** Segments the user may upload into. Default ['inbound'] (partner behavior). */
  readonly uploadableSegments?: readonly Segment[];
}

export function FileExchangeView({
  client,
  openUrl,
  uploadableSegments = ['inbound'],
}: FileExchangeViewProps): React.JSX.Element {
  const [segment, setSegment] = useState<Segment>('inbound');
  const [files, setFiles] = useState<FxFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open =
    openUrl ??
    ((url: string) => {
      // The presigned GET carries Content-Disposition: attachment, so an anchor click
      // downloads in place. Using window.open('_blank') instead flashed a tab that the
      // download immediately auto-closed. No target → no tab.
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFiles(await client.list(segment));
    } catch {
      setFiles([]);
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [client, segment]);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upload = useFileUpload({ client, segment, onComplete: () => void refresh() });
  const onDownload = useCallback(
    async (key: string) => {
      open(await client.presignDownload(segment, key));
    },
    [client, segment, open],
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(['inbound', 'outbound'] as const).map((s) => (
          <Button
            key={s}
            tone={segment === s ? 'primary' : 'secondary'}
            onClick={() => setSegment(s)}
            className="capitalize"
          >
            {s}
          </Button>
        ))}
        <Button tone="secondary" onClick={() => void refresh()} disabled={loading}>
          Refresh
        </Button>
      </div>
      {uploadableSegments.includes(segment) && (
        <UploadDropzone
          onFiles={(f) => void upload.uploadFiles(f)}
          uploading={upload.uploading}
          progress={upload.progress}
          error={upload.error}
        />
      )}
      <FileTable files={files} loading={loading} onDownload={(key) => void onDownload(key)} />
      <Snackbar
        open={error !== null}
        tone="danger"
        message={
          <>
            {error}{' '}
            <Button tone="link" onClick={() => void refresh()}>
              Retry
            </Button>
          </>
        }
        onClose={() => setError(null)}
      />
    </div>
  );
}
